import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import Map from 'ol/Map';
import View from 'ol/View';
import { ImageWMS, OSM, Source, Vector, XYZ } from 'ol/source';
import Tile from 'ol/layer/Tile';
import { defaults } from 'ol/control/defaults';
import { MapService } from '../map.service';
import { MousePosition, ScaleLine } from 'ol/control'
import { createStringXY } from 'ol/coordinate';
import { fromLonLat } from 'ol/proj';
import VectorLayer from 'ol/layer/Vector';
import Draw, { createBox } from 'ol/interaction/Draw';
import { Type } from 'ol/geom/Geometry';
import { environment } from './../../environments/environment';
import { TabService } from '../tab.service';
import { FeatureInfo } from '../data/featureinfo';
import { AgChartOptions, AgLineSeriesOptions } from 'ag-charts-community';
import { first } from 'rxjs';
import { pcaReconstructSingle, setNoData } from '../util/pca';

@Component({
  standalone: false,  // this is now required when using NgModule
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.css']
})

export class MapComponent implements OnInit {

  mapboxToken: string = environment.mapboxToken;
  MAPBOX_SATELLITE_STREETS_URL: string = 'https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v11/tiles/{z}/{x}/{y}?access_token=' + this.mapboxToken;
  MAPBOX_SATELLITE_URL: string = 'https://api.mapbox.com/styles/v1/mapbox/satellite-v9/tiles/{z}/{x}/{y}?access_token=' + this.mapboxToken;
  MAPBOX_STREETS_URL: string = 'https://api.mapbox.com/styles/v1/mapbox/streets-v11/tiles/{z}/{x}/{y}?access_token=' + this.mapboxToken;
  MAPBOX_TERRAIN_URL: string = 'https://api.mapbox.com/styles/v1/mapbox/outdoors-v11/tiles/{z}/{x}/{y}?access_token=' +this. mapboxToken;

  EPSG_3857 = "EPSG:3857"; // Spherical Mercator projection used by most web map applications (e.g Google, OpenStreetMap, Mapbox).
  EPSG_4326 = "EPSG:4326"; // Standard coordinate system used in cartography, geodesy, and navigation (including GPS).

  SHAPE = {
    NONE: {},
    POLYGON : {type: 'Polygon', img: 'images/polygon.png', location: 'polygon-selection', name:"Polygon"},
    BOX : {type: 'LineString', img: 'images/box.png', location: 'box-selection', name:"Square"}
  };

  /* Padding for bottom and sidebar when padding to feature */
  padding = [0, 0, 300, 0];

  draw: Draw|undefined; // global so we can remove it later
  drawing: boolean = false;

  constructor(public mapService: MapService, public tabService: TabService, private ref: ChangeDetectorRef) {
    /* Set active layer on map load */
    if (this.mapService.mapstore.type.active === "Satellite Street") {
        this.activeLayer = this.layerSatSM;
    } else if (this.mapService.mapstore.type.active === "Satellite") {
      this.activeLayer = this.layerSatM;
    } else if (this.mapService.mapstore.type.active === "Street") {
      this.activeLayer = this.layerSM;
    } else if (this.mapService.mapstore.type.active === "Terrain") {
      this.activeLayer = this.layerTM;
    } else if (this.mapService.mapstore.type.active === "Open Street") {
      this.activeLayer = this.layerOSM;
    }
    this.searchBoxSource = new Vector({ 
      features:this.mapService.searchLayerFeatures,
      wrapX: false
    });
    this.searchBoxLayer = new VectorLayer({
      source: this.searchBoxSource,
      style: this.mapService.searchBoxStyle,
      zIndex: 2,
    });
    this.searchBoxLayer.on('change', this.searchBoxChanged);
  }

  /** ----- MAP LAYER TYPES ----- **/

  searchBoxSource: Vector;
  searchBoxLayer: VectorLayer;

  activeLayer: any = undefined;

  layerOSM = new Tile({
    source: new OSM()
  });

  layerSatSM = new Tile({
    source: new XYZ({
      tileSize: [512, 512],
      url: this.MAPBOX_SATELLITE_STREETS_URL
    })
  });

  layerSatM = new Tile({
    source: new XYZ({
      tileSize: [512, 512],
      url: this.MAPBOX_SATELLITE_URL
    })
  });

  layerSM = new Tile({
    source: new XYZ({
      tileSize: [512, 512],
      url: this.MAPBOX_STREETS_URL
    })
  });

  layerTM = new Tile({
    source: new XYZ({
      tileSize: [512, 512],
      url: this.MAPBOX_TERRAIN_URL
    })
  });

  /** ----- END OF MAP LAYER TYPES ----- **/

  /** ----- MOUSE POSITION COORDINATES ----- **/
  mousePositionControl = new MousePosition({
      coordinateFormat: createStringXY(4),
      projection: this.EPSG_4326
  });
  /** ----- END OF MOUSE POSITION COORDINATES ----- **/

  ngOnInit(): void {
    this.mapService.map = new Map({
      layers: [ this.activeLayer ],
      target: 'map',
      view: new View({ 
        center: fromLonLat(this.mapService.mapstore.location),
        zoom: this.mapService.mapstore.zoom
      }),
      controls: defaults({
        attributionOptions: ({
            collapsible: false
        })
      }).extend([
        //        new $scope.draw(SHAPE.POLYGON),
        //        new $scope.draw(SHAPE.BOX),
        new ScaleLine(),
        this.mousePositionControl
    ])});
    var resultsLayer = new VectorLayer({
      source: new Vector({
          features: this.mapService.resultLayerFeatures
      }),
      style: this.mapService.resultStyle,
      zIndex: 1
    });
    this.mapService.map.addLayer(resultsLayer);
    this.mapService.map.addLayer(this.searchBoxLayer);
    // Bounding box drawing
    let value:Type = 'Circle';
    let geometryFunction = createBox();
    this.draw = new Draw({
        source: this.searchBoxSource,
        type: value,
        geometryFunction: geometryFunction,
      });
    // Map click function
    const mymapservice = this.mapService;
    mymapservice.map?.on("singleclick", (evt) => {
      if (!mymapservice.map) {
        return;
      }
      const viewResolution = mymapservice.map.getView().getResolution();
      const layers = mymapservice.map.getAllLayers();
      const now = Date.now();
      this.currentTimestamp = now;
      // Reset features
      this.featureInfoArray = [];
      let wmsLayers:any[] = [];
      for (let layer of layers) {
        let source = layer.getSource();
        if (source instanceof ImageWMS) {
          wmsLayers.push(layer);
          let wmsRec = this.mapService.getWMSRecordForLayer(layer);
          let fi = new FeatureInfo();
          fi.wmslayer = layer;
          //console.log(layer);
          if (wmsRec && 'title' in wmsRec) {
            fi.title = String(wmsRec['title']);
          } else {
            fi.title = String(layer);
          }
          // Dots in title caused problems for some strings
          fi.title = fi.title.replaceAll(".", "_");
          if ('pca_components' in wmsRec?.properties && 'pca_mean' in wmsRec?.properties) {
            fi.pca_components = JSON.parse(wmsRec?.properties.pca_components);
            fi.pca_mean = JSON.parse(wmsRec?.properties.pca_mean);
            fi.pca_nodata = wmsRec?.properties.pca_nodata;
          }
          this.featureInfoArray.push(fi);
        }
      }
      for (let layer of wmsLayers) {
        const url = layer.getSource()?.getFeatureInfoUrl(
          evt.coordinate,
          viewResolution ?? 1,
          mymapservice.map.getView().getProjection(),
          {
            INFO_FORMAT: "application/json"
          }
        );
        if (url) {
          fetch(url)
            .then((response) => response.json())
            .then((data) => {
              //console.log("GFI result:", data);
              //this.showPopup(evt.coordinate, data);
              this.showFeatureInfoData(evt.coordinate, now, data, layer);
            })
            .catch((err) => console.error(err));
        }
      }
    });


    this.draw.on('drawstart', this.handleDrawStartEvent);
    this.draw.on('drawend', this.handleDrawEndEvent);
  }

  // Collected results of the most recent get feature info request
  featureInfoArray: FeatureInfo[] = [];
  // The timestamp of the most recent get feature info request
  currentTimestamp: number = 0;

  showFeatureInfoData(coord: number[], timestamp: any, data: any, layer:any): void {
    // Data for each layer is retrieved asynchronously 
    // Each click has a timestamp so that the data of each click can be combined

    if (timestamp != this.currentTimestamp) {
      // Results from an outdated query, ignore
      return;
    }
    // Process current data
    // Match the result to the correct FeatureInfo item
    // Remove items with no data from the list
    for (let fi of this.featureInfoArray) {
      if (fi.wmslayer == layer) {
        if (data.features.length == 0) {
          const index = this.featureInfoArray.indexOf(fi, 0);
          if (index > -1) {
            this.featureInfoArray.splice(index, 1);
          }
        } else {
          for (let attr of data.features) {
            for (const key in attr.properties) {
              fi.values.push(attr.properties[key]);
            }
          }
        }
        break;
      }
    }
    //console.log(this.featureInfoArray);
    if (this.featureInfoArray.length > 0) {
      // Build the feature graph
      let series:AgLineSeriesOptions[] = [];
      let maxBands:number = 0;
      for (let fi of this.featureInfoArray) {
        //console.log(fi);
        // Reconstruct PCA if defined
        if (fi.values.length > 0 && fi.pca_components && fi.pca_mean) {
          fi.values = pcaReconstructSingle(fi.values, fi.pca_components, fi.pca_mean, fi.pca_nodata);
        }
        // Build series object
        if (fi.title) {
          series.push(
            {
              type: "line",
              xKey: "band",
              yKey: fi.title,
            } as AgLineSeriesOptions
          );
          // Keep track of maximum band count
          maxBands = Math.max(maxBands, fi.values.length);
        }
      }
      let data = [];
      for (let i = 0; i < maxBands; i++) {
        let bandId:string = "B"+(i+1);
        var d: {[k: string]: any} = { band: bandId };
        for (let fi of this.featureInfoArray) {
          if (fi.title) {
            if (i < fi.values.length) {
              d[fi.title] = fi.values[i];
            } else {
              d[fi.title] = undefined;
            }
          }
        }
        data.push(d);
      }
      let featureInfoChartData: AgChartOptions = {
        // Data: Data to be displayed in the chart
        data: data,
        // Series: Defines which chart type and data to use
        series: series,
        minHeight: 150
      };
      //console.log(featureInfoChartData);
      // Make sure the bottom view is visible, otherwise data panel is not visible
      this.tabService.showBottomView();
      // Update the visible data (the panel gets its input from mapservice)
      this.mapService.featureInfoArray = this.featureInfoArray;
      this.mapService.featureInfoChartData = featureInfoChartData;
    } else {
      // Hide the feature data panel when there is no data at clicked location 
      this.mapService.featureInfoArray = undefined;
      this.mapService.featureInfoChartData = undefined;
    }
    this.ref.markForCheck();
    //console.log(this.mapService.featureInfoArray);
  }

  private searchBoxChanged = (event:any) => {
    let bbox = this.mapService.getSearchBoundingBox();
    if (bbox) {
      let s = 'Latitude ' + bbox[0].toFixed(3) + ' - ' + bbox[2].toFixed(3)
        + '\nLongitude ' + bbox[1].toFixed(3) + ' - ' + bbox[3].toFixed(3);
      this.mapService.mapstore.aoi.selectedArea = s;
    } else {
      this.mapService.mapstore.aoi.selectedArea = 'No limit';
    }
  }

  private handleDrawStartEvent = (event:any) => {
    this.clearSearchPolygon();
  }
  private handleDrawEndEvent = (event:any) => {
    this.removeDraw();
  }

  clearSearchPolygon(): void {
    this.mapService.searchLayerFeatures.clear();
  }

  removeRedundantLayers(newLayer: any): void {
    var layers = [this.layerSatSM, this.layerSatM, this.layerSM, this.layerTM, this.layerOSM];
    layers.filter(layer => layer != newLayer).forEach(layer => this.mapService.map!.removeLayer(layer));
  };

  setMapType(newType: string): void {
    if (newType === 'SatSM' && this.mapService.mapstore.type.active !== "Satellite Street") {
        this.removeRedundantLayers(this.layerSatSM);
        this.mapService.map!.getLayers().insertAt(0, this.layerSatSM);
        this.mapService.mapstore.type.active = "Satellite Street";
    } else if (newType === 'SatM' && this.mapService.mapstore.type.active !== "Satellite") {
      this.removeRedundantLayers(this.layerSatM);
        this.mapService.map!.getLayers().insertAt(0, this.layerSatM);
        this.mapService.mapstore.type.active = "Satellite";
    } else if (newType === 'SM' && this.mapService.mapstore.type.active !== "Street") {
        this.removeRedundantLayers(this.layerSM);
        this.mapService.map!.getLayers().insertAt(0, this.layerSM);
        this.mapService.mapstore.type.active = "Street";
    } else if (newType === 'TM' && this.mapService.mapstore.type.active !== "Terrain") {
        this.removeRedundantLayers(this.layerTM);
        this.mapService.map!.getLayers().insertAt(0, this.layerTM);
        this.mapService.mapstore.type.active = "Terrain";
    } else if (newType === 'OSM' && this.mapService.mapstore.type.active !== "Open Street") {
        this.removeRedundantLayers(this.layerOSM);
        this.mapService.map!.getLayers().insertAt(0, this.layerOSM);
        this.mapService.mapstore.type.active = "Open Street";
    }
  };

  clearMap(): void {
    this.mapService.clearResultLayerFeatures();
    this.clearSearchPolygon();
  }

  toggleDraw() {
    if (this.draw) {
      if (this.drawing) {
        this.removeDraw();
      } else {
        this.mapService.map?.addInteraction(this.draw);
        this.drawing = true;
      }
    }
  }
  
  removeDraw() {
    if (this.draw) {
      this.mapService.map?.removeInteraction(this.draw);
      this.drawing = false;
    }
  }

}
